import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { TableOptions } from "../../../../shared-kernel/components/Table";
import {
  createAdapterToBankTransactionClassification,
  createAdapterToBankTransactionClassificationEndpoint,
  createAdapterToFieldBankTransactionClassificationEndpoint
} from "../adapters/bankTransactionClassification.adapter";
import { BankTransactionClassification, BankTransactionClassificationEndpoint, EndpointBankTransactionClassificationFiltered } from "../domain/BankTransactionClassification.model";
import { appConfig } from "../../../../config";
import { EndpointBankTransactionClassification } from "../domain/BankTransactionClassification.model";
import { createAdapterToFilterBackend } from "../../../../utilities/paramsAdapter";

const BASE_BANK_TRANSACTION_CLASSIFICATION_URL = '/entidadesfinancieras/clasificacionesmovimientosbancarios';
const BASE_BANK_TRANSACTION_CLASSIFICATION_MUTATION_URL = '/entidadesfinancieras/clasificacionmovimientobancario';
const BASE_PERSONA_URL = '/general/personas';

export const BASE_EXPORT_URL = `${appConfig.API_NODE_URL}/entidadesfinancieras/clasificacionesmovimientosbancarios/export`;

export const bankTransactionClassificationApi = createApi({
  reducerPath: "bankTransactionClassificationApi",
  keepUnusedDataFor: 0,
  baseQuery: fetchBaseQuery({
    baseUrl: appConfig.API_NODE_URL,
    prepareHeaders: (headers: Headers) => {
      const APP_TOKEN = "app_token";
      const token = localStorage.getItem(APP_TOKEN);

      if (token) {
        headers.set("authorization", `${token}`);
      }

      return headers;
    },
    credentials: "include",
  }),
  refetchOnMountOrArgChange: true,
  tagTypes: ["BankTransactionsClassification", "BankTransactionClassification"],
  endpoints: (builder) => ({
    getBankTransactionClassification: builder.query<BankTransactionClassification, number | undefined>({
      query: (id) => `${BASE_BANK_TRANSACTION_CLASSIFICATION_MUTATION_URL}/${id}`,
      transformResponse: (response: EndpointBankTransactionClassification) => {
        return createAdapterToBankTransactionClassification(response.Content)
      },
      providesTags: (_res, _err, id) => [{type: "BankTransactionClassification", id: id?.toString()}],
    }),
    getBankTransactionsClassification: builder.query<any, TableOptions<BankTransactionClassification>|undefined>({
      query: (params: TableOptions<BankTransactionClassification>) => {
        const { page, pageSize, order, search, filters } = params || {};
        const adaptersFilter = filters?.map( f => createAdapterToFilterBackend(f,createAdapterToFieldBankTransactionClassificationEndpoint));
        const serializedFilters = adaptersFilter ? JSON.stringify(adaptersFilter) : undefined;
        const serializedOrder = order ? JSON.stringify({field:createAdapterToFieldBankTransactionClassificationEndpoint(order.field), order:order.order}) : undefined;
        return {
          url:`${BASE_BANK_TRANSACTION_CLASSIFICATION_URL}/filtros`,
          params: {
            page:page? page - 1: 0,
            pageSize,
            order: serializedOrder,
            search,
            filters:serializedFilters
          },
        }
      },
      providesTags: ["BankTransactionsClassification"],
      transformResponse: (response: EndpointBankTransactionClassificationFiltered, _meta, params: TableOptions<BankTransactionClassification>) => {
        const page = params?.page ?? 1;
        const endpointResponse = response?.rows?.map((bankTransactionClassification: BankTransactionClassificationEndpoint) =>
            createAdapterToBankTransactionClassification(bankTransactionClassification)
        );
        return {
          data: endpointResponse,
          page,
          total: response.total,
        };
      },
    }),
    getBankTransactionsClassificationCuits: builder.query<any, any>({
      query: (bankTransactionClassificationId) => {
        return {
          url: `${BASE_BANK_TRANSACTION_CLASSIFICATION_URL}/getCuitsByClasificacion/${bankTransactionClassificationId}`
        }
      }
    }),
    getBankTransactionsClassificationPersonas: builder.query<any, any>({
      query: () => {
        return {
          url: `${BASE_PERSONA_URL}`
        }
      }
    }),
    createBankTransactionClassification: builder.mutation<any, BankTransactionClassification>({
      query: (data) => ({
        url: `${BASE_BANK_TRANSACTION_CLASSIFICATION_MUTATION_URL}`,
        method: "POST",
        body: {
          "Content": createAdapterToBankTransactionClassificationEndpoint(data)
        },
      }),
      invalidatesTags: ["BankTransactionsClassification"],
    }),
    createBankTransactionClassificationCuits: builder.mutation<any, any>({
      query: (data) => ({
        url: `${BASE_BANK_TRANSACTION_CLASSIFICATION_URL}/cuits`,
        method: "POST",
        body: {
          "Content": data
        },
      }),
      invalidatesTags: ["BankTransactionClassification"],
    }),
    updateBankTransactionClassification: builder.mutation<any, BankTransactionClassification>({
      query: (data: BankTransactionClassification) => ({
        url: `${BASE_BANK_TRANSACTION_CLASSIFICATION_MUTATION_URL}`,
        method: "PUT",
        body: {
          "Content": createAdapterToBankTransactionClassificationEndpoint(data)
        },
      }),
      invalidatesTags: (_res, _err, data: BankTransactionClassification) => ["BankTransactionsClassification", {type: 'BankTransactionClassification', id: data?.id?.toString()}],
    }),
    deleteBankTransactionClassification: builder.mutation<void, number>({
      query: (id) => ({
        url: `${BASE_BANK_TRANSACTION_CLASSIFICATION_MUTATION_URL}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BankTransactionsClassification"],
    }),
    deleteBankTransactionClassificationCuits: builder.mutation<void, any>({
      query: (body: any) => ({
        url: `${BASE_BANK_TRANSACTION_CLASSIFICATION_URL}/deleteClasificacionCuits`,
        method: "DELETE",
        body
      }),
      invalidatesTags: ["BankTransactionsClassification"],
    }),
  }),
});

export const {
  useGetBankTransactionClassificationQuery,
  useGetBankTransactionsClassificationQuery,
  useGetBankTransactionsClassificationCuitsQuery,
  useGetBankTransactionsClassificationPersonasQuery,
  useCreateBankTransactionClassificationMutation,
  useCreateBankTransactionClassificationCuitsMutation,
  useUpdateBankTransactionClassificationMutation,
  useDeleteBankTransactionClassificationMutation,
  useDeleteBankTransactionClassificationCuitsMutation
} = bankTransactionClassificationApi;
